import { useMemo, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

const getUTMFields = () => {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const fields = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_adset"].forEach((k) => {
    const v = p.get(k);
    if (v) fields[k] = v;
  });
  return fields;
};

const trackSubmitLead = (includeLinkedIn = false) => {
  if (typeof window === "undefined") return;
  // Meta Pixel — custom event
  if (typeof window.fbq === "function") {
    window.fbq("trackCustom", "submit_lead");
  }
  // GA4 via gtag, falling back to GTM dataLayer
  if (typeof window.gtag === "function") {
    window.gtag("event", "submit_lead");
  } else if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push({ event: "submit_lead" });
  }
  // LinkedIn — only when UTM present and API returned 200
  if (includeLinkedIn && typeof window.lintrk === "function") {
    window.lintrk("track", { conversion_id: 29337076 });
  }
};

const QUIZ_API = import.meta.env.VITE_QUIZ_API_URL;
const CMS_API = import.meta.env.VITE_CMS_API_URL;

const PERSONAL_EMAIL_DOMAINS = [
  "gmail.com", "yahoo.com", "hotmail.com", "outlook.com",
  "icloud.com", "aol.com", "protonmail.com", "mail.com", "live.com", "googlemail.com",
];

const INDUSTRIES = [
  "Advertising & Marketing", "Agency / Consulting", "Automotive",
  "Banking & Financial Services", "Construction & Real Estate", "Consumer Goods",
  "E-commerce & Retail", "Education", "Energy & Utilities", "Entertainment & Media",
  "Fashion & Apparel", "Food & Beverage", "Government & Public Sector",
  "Healthcare & Pharmaceuticals", "Hospitality & Travel", "Human Resources",
  "Insurance", "Legal Services", "Logistics & Supply Chain", "Manufacturing",
  "Non-profit / NGO", "Professional Services", "SaaS / Software", "Sports & Fitness",
  "Technology", "Telecommunications", "Transportation", "Other",
];

const COMPANY_SIZES = ["1–49", "50–249", "250–999", "1,000–9,999", "10,000+"];

const TEAM_VALUES = [
  "Product", "Engineering", "Marketing", "Human Resources", "Sales",
  "Operations", "Finance", "Customer Support", "Design", "Legal", "IT", "Other",
];

const COUNTRY_CODES = ["IND +91", "USA +1", "UK +44", "AUS +61", "JPN +81"];

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function pad(n) { return String(n).padStart(2, "0"); }
function toISODate(y, m, d) { return `${y}-${pad(m + 1)}-${pad(d)}`; }

function validateWorkEmail(email) {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address";
  const domain = email.split("@")[1]?.toLowerCase();
  if (PERSONAL_EMAIL_DOMAINS.includes(domain)) return "Please use your work email, not a personal one";
  return null;
}

/* ── Booking Step ── */
function BookingStep({ adminToken, adminName, adminEmail, onSkip, onBooked }) {
  const [earliestDate, setEarliestDate] = useState("");
  const [viewYear, setViewYear] = useState(null);
  const [viewMonth, setViewMonth] = useState(null);
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [booking, setBooking] = useState(false);
  const [bookingError, setBookingError] = useState("");

  const timeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  useEffect(() => {
    fetch(`${QUIZ_API}/api/admin/${adminToken}/booking/config`)
      .then((r) => r.json())
      .then((cfg) => {
        setEarliestDate(cfg.earliestDate);
        const [y, m] = cfg.earliestDate.split("-").map(Number);
        setViewYear(y);
        setViewMonth(m - 1);
      })
      .catch(() => setSlotsError("Could not load booking availability."));
  }, [adminToken]);

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSlotsError("");
    setSlots([]);
    setSelectedSlot("");
    fetch(`${QUIZ_API}/api/admin/${adminToken}/booking/slots?date=${selectedDate}`)
      .then((r) => r.json())
      .then((data) => {
        const daySlots = data.data?.slots?.[selectedDate] ?? [];
        setSlots(daySlots.map((s) => s.time));
      })
      .catch(() => setSlotsError("Could not load slots for this date."))
      .finally(() => setLoadingSlots(false));
  }, [adminToken, selectedDate]);

  const confirmBooking = async () => {
    if (!selectedSlot) return;
    setBooking(true);
    setBookingError("");
    try {
      const res = await fetch(`${QUIZ_API}/api/admin/${adminToken}/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start: selectedSlot, name: adminName, email: adminEmail, timeZone }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to create booking");
      }
      onBooked();
    } catch (e) {
      setBookingError(e.message || "Failed to create booking.");
    } finally {
      setBooking(false);
    }
  };

  if (viewYear === null || viewMonth === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-white/50 text-sm font-fira-sans">Loading availability…</p>
      </div>
    );
  }

  const [ey, em] = earliestDate.split("-").map(Number);
  const isEarliestMonth = viewYear === ey && viewMonth === em - 1;
  const firstWeekday = new Date(Date.UTC(viewYear, viewMonth, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(viewYear, viewMonth + 1, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    const date = toISODate(viewYear, viewMonth, day);
    const weekday = new Date(Date.UTC(viewYear, viewMonth, day)).getUTCDay();
    cells.push({ day, date, disabled: date < earliestDate || weekday === 0 });
  }

  const goPrev = () => {
    if (isEarliestMonth) return;
    const p = new Date(Date.UTC(viewYear, viewMonth - 1, 1));
    setViewYear(p.getUTCFullYear()); setViewMonth(p.getUTCMonth());
  };
  const goNext = () => {
    const n = new Date(Date.UTC(viewYear, viewMonth + 1, 1));
    setViewYear(n.getUTCFullYear()); setViewMonth(n.getUTCMonth());
  };

  return (
    <div className="space-y-6 font-fira-sans">
      <div>
        <p className="text-white/50 text-xs uppercase tracking-[1.5px] mb-1">Step 2</p>
        <h3 className="text-white text-[18px] font-medium">Book your report walkthrough</h3>
        <p className="text-white/50 text-sm mt-1">
          Pick a date and time for your team&apos;s AI assessment walkthrough. Sundays excluded.
        </p>
      </div>

      {/* Calendar */}
      <div className="border border-white/15 rounded-[4px] p-4">
        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <button
            type="button"
            onClick={goPrev}
            disabled={isEarliestMonth}
            className="text-white/40 hover:text-white disabled:opacity-20 text-lg px-2 transition-colors"
          >
            ←
          </button>
          <span className="text-white text-sm font-medium">
            {MONTH_NAMES[viewMonth]} {viewYear}
          </span>
          <button
            type="button"
            onClick={goNext}
            className="text-white/40 hover:text-white text-lg px-2 transition-colors"
          >
            →
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEKDAYS.map((w) => (
            <div key={w} className="text-center text-white/30 text-xs py-1">{w}</div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7 gap-1">
          {cells.map((cell, i) =>
            cell === null ? (
              <div key={`b-${i}`} />
            ) : (
              <button
                key={cell.date}
                type="button"
                disabled={cell.disabled}
                onClick={() => setSelectedDate(cell.date)}
                className={`text-center text-sm py-2 rounded-[4px] transition-colors
                  ${cell.disabled ? "text-white/20 cursor-not-allowed" :
                    selectedDate === cell.date
                      ? "bg-[#B8EF43] text-black font-medium"
                      : "text-white hover:bg-white/10"
                  }`}
              >
                {cell.day}
              </button>
            )
          )}
        </div>

        {/* Timezone */}
        <p className="text-white/30 text-xs mt-3">🕐 {timeZone}</p>
      </div>

      {/* Slots */}
      {selectedDate && (
        <div>
          {loadingSlots && <p className="text-white/50 text-sm">Loading available times…</p>}
          {slotsError && <p className="text-red-400 text-sm">{slotsError}</p>}
          {!loadingSlots && !slotsError && slots.length === 0 && (
            <p className="text-white/50 text-sm">No open slots for this date — try another day.</p>
          )}
          {!loadingSlots && slots.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {slots.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSelectedSlot(s)}
                  className={`text-sm py-2 rounded-[4px] border transition-colors
                    ${selectedSlot === s
                      ? "bg-[#B8EF43] text-black border-[#B8EF43] font-medium"
                      : "border-white/20 text-white hover:border-white/40"
                    }`}
                >
                  {new Date(s).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {bookingError && <p className="text-red-400 text-sm">{bookingError}</p>}

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onSkip}
          className="flex-1 border border-white/20 text-white/60 hover:text-white py-3 rounded-[2px] text-sm transition-colors"
        >
          Skip for now
        </button>
        <button
          type="button"
          onClick={confirmBooking}
          disabled={!selectedSlot || booking}
          className="flex-1 bg-[#B8EF43] hover:bg-lime-300 text-black font-medium py-3 rounded-[2px] text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {booking ? "Booking…" : "Confirm booking"}
        </button>
      </div>
    </div>
  );
}

BookingStep.propTypes = {
  adminToken: PropTypes.string.isRequired,
  adminName: PropTypes.string.isRequired,
  adminEmail: PropTypes.string.isRequired,
  onSkip: PropTypes.func.isRequired,
  onBooked: PropTypes.func.isRequired,
};

/* ── Teams multi-select dropdown ── */
function TeamsDropdown({ selected, onToggle, error }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const label = selected.length === 0
    ? "Select teams"
    : selected.length === 1
      ? selected[0]
      : `${selected.length} teams selected`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between rounded-[2px] px-3 py-2 bg-transparent border text-sm font-fira-sans focus:outline-none focus:ring-2 focus:ring-[#B8EF43] transition-colors
          ${error ? "border-red-500" : "border-white/30"}
          ${selected.length > 0 ? "text-white" : "text-white/40"}`}
      >
        <span>{label}</span>
        <span className={`ml-2 transition-transform text-white/40 ${open ? "rotate-180" : ""}`}>▾</span>
      </button>

      {open && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-[#141516] border border-white/20 rounded-[4px] shadow-xl max-h-56 overflow-y-auto">
          {TEAM_VALUES.map((team) => {
            const checked = selected.includes(team);
            return (
              <button
                key={team}
                type="button"
                onClick={() => onToggle(team)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-white/5 transition-colors"
              >
                <span className={`w-4 h-4 flex-shrink-0 rounded-[2px] border flex items-center justify-center transition-colors
                  ${checked ? "bg-[#B8EF43] border-[#B8EF43]" : "border-white/30"}`}>
                  {checked && <span className="text-black text-[10px] font-bold leading-none">✓</span>}
                </span>
                <span className={checked ? "text-white" : "text-white/60"}>{team}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

TeamsDropdown.propTypes = {
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
  error: PropTypes.string,
};

/* ── Main Form ── */
const B2BAssessmentForm = () => {
  const [stage, setStage] = useState("form"); // form | booking | done
  const [bookingResult, setBookingResult] = useState(null);

  const [formData, setFormData] = useState({
    adminName: "",
    adminEmail: "",
    companyName: "",
    companyLocation: "",
    companyIndustry: "",
    companySize: "",
    teams: [],
    numberOfUsers: "",
    countryCode: "IND +91",
    adminPhone: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  // Any UTM → enquiry-only flow (CMS lead API only); no UTM → full assessment flow
  const hasUTM = useMemo(() => Object.keys(getUTMFields()).length > 0, []);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const toggleTeam = (team) => {
    setFormData((prev) => ({
      ...prev,
      teams: prev.teams.includes(team)
        ? prev.teams.filter((t) => t !== team)
        : [...prev.teams, team],
    }));
    if (errors.teams) setErrors((prev) => ({ ...prev, teams: "" }));
  };

  const canSubmit = useMemo(() => {
    if (!formData.adminName.trim()) return false;
    if (!formData.adminEmail.trim() || validateWorkEmail(formData.adminEmail)) return false;
    if (!formData.companyName.trim()) return false;
    if (!formData.companyIndustry) return false;
    if (!formData.companySize) return false;
    if (formData.teams.length === 0) return false;
    if (!formData.adminPhone.trim()) return false;
    if (!formData.numberOfUsers || Number(formData.numberOfUsers) < 1) return false;
    return true;
  }, [formData]);

  const validate = () => {
    const e = {};
    if (!formData.adminName.trim()) e.adminName = "Full name is required";
    if (!formData.adminEmail.trim()) e.adminEmail = "Work email is required";
    else { const err = validateWorkEmail(formData.adminEmail); if (err) e.adminEmail = err; }
    if (!formData.companyName.trim()) e.companyName = "Company name is required";
    if (!formData.companyIndustry) e.companyIndustry = "Please select an industry";
    if (!formData.companySize) e.companySize = "Please select company size";
    if (formData.teams.length === 0) e.teams = "Select at least one team";
    if (!formData.adminPhone.trim()) e.adminPhone = "Phone number is required";
    if (!formData.numberOfUsers || Number(formData.numberOfUsers) < 1) e.numberOfUsers = "Enter expected number of participants";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError("");
    const utmFields = getUTMFields();

    const sendCmsLead = () =>
      fetch(`${CMS_API}/api/outskill-assessment-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.adminName,
          workEmail: formData.adminEmail,
          companyName: formData.companyName,
          companyLocation: formData.companyLocation,
          companyIndustry: formData.companyIndustry,
          companySize: formData.companySize,
          teams: formData.teams,
          numberOfUsers: formData.numberOfUsers,
          countryCode: formData.countryCode,
          adminPhone: formData.adminPhone,
          utmSource: utmFields.utm_source || "",
          utmMedium: utmFields.utm_medium || "",
          utmCampaign: utmFields.utm_campaign || "",
          utmAdset: utmFields.utm_adset || "",
          utmContent: utmFields.utm_content || "",
          utmTerm: utmFields.utm_term || "",
        }),
      });

    // Enquiry-only flow: just the CMS lead API
    if (hasUTM) {
      try {
        const res = await sendCmsLead();
        if (!res.ok) throw new Error("Submission failed");
        trackSubmitLead(true);
        setStage("done");
      } catch (err) {
        setSubmitError(err.message || "Something went wrong. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    // Fire-and-forget Slack notification via CMS — independent of /api/companies result
    sendCmsLead().catch(() => {});

    try {
      const res = await fetch(`${QUIZ_API}/api/companies`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyName: formData.companyName,
          companyLocation: formData.companyLocation || undefined,
          companyIndustry: formData.companyIndustry || undefined,
          companySize: formData.companySize || undefined,
          adminName: formData.adminName,
          adminEmail: formData.adminEmail,
          adminPhone: formData.adminPhone
            ? `${formData.countryCode.split(" ")[1]} ${formData.adminPhone}`
            : undefined,
          teams: formData.teams,
          numberOfUsers: formData.numberOfUsers ? Number(formData.numberOfUsers) : undefined,
          utmSource: utmFields.utm_source,
          utmMedium: utmFields.utm_medium,
          utmCampaign: utmFields.utm_campaign,
          utmTerm: utmFields.utm_term,
          utmContent: utmFields.utm_content,
          utmAdset: utmFields.utm_adset,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Submission failed");
      trackSubmitLead();
      setBookingResult({
        adminToken: data.company.admin_token,
        companyName: data.company.company_name,
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        assessmentUrl: data.assessmentUrl,
        adminUrl: data.adminUrl,
      });
      setStage("booking");
    } catch (err) {
      setSubmitError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputBase =
    "w-full rounded-[2px] px-3 py-2 bg-transparent border focus:outline-none focus:ring-2 focus:ring-[#B8EF43] text-white placeholder-white/40 font-fira-sans text-sm";
  const labelBase = "block text-sm font-medium text-white mb-2";
  const errorClass = "mt-1 text-xs text-red-400";

  return (
    <section
      id="business-contact"
      className="w-full bg-[#08090A] font-fira-sans"
      style={{
        borderTop: "1px solid rgba(255,255,255,0.15)",
        borderBottom: "1px solid rgba(255,255,255,0.15)",
      }}
    >
      <div
        className="w-[90%] md:w-auto mx-auto md:mx-[153px]"
        style={{
          borderLeft: "1px solid rgba(255,255,255,0.15)",
          borderRight: "1px solid rgba(255,255,255,0.15)",
        }}
      >
        <div className="px-4 md:pl-[60px] md:pr-[60px] py-16">
          <div className="flex flex-col md:flex-row gap-12">

            {/* Left */}
            <div className="md:w-[40%]">
              <p className="text-white/70 uppercase tracking-[1.75px] text-sm mb-4">
                INVEST IN GROWTH
              </p>
              <h2 className="text-white text-[30px] md:text-[38px] font-medium leading-tight">
                Ready to Start Your Growth Journey?
              </h2>
              {stage === "form" && (
                <p className="text-white/50 text-sm mt-4 leading-relaxed max-w-sm">
                  Register your company to get a shareable AI readiness assessment link for your team - takes 2 minutes.
                </p>
              )}
            </div>

            {/* Right */}
            <div className="md:w-[60%]">

              {/* ── Done ── */}
              {stage === "done" && (
                <div className="border border-white/20 rounded-[4px] p-8 text-center space-y-3">
                  <div className="text-3xl">✓</div>
                  <h3 className="text-white text-xl font-medium">You&apos;re all set!</h3>
                  <p className="text-white/60 text-sm">
                    {hasUTM
                      ? "Thanks for your enquiry. We'll be in touch soon."
                      : "Your team's assessment link is ready. We'll be in touch soon."}
                  </p>
                  {bookingResult?.assessmentUrl && (
                    <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                      <p className="text-white/40 text-xs uppercase tracking-wider">Assessment link</p>
                      <a
                        href={bookingResult.assessmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full bg-[#B8EF43] hover:bg-lime-300 text-black font-medium py-3 rounded-[2px] text-sm transition-colors"
                      >
                        Open Assessment Link →
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* ── Booking ── */}
              {stage === "booking" && bookingResult && (
                <BookingStep
                  adminToken={bookingResult.adminToken}
                  adminName={bookingResult.adminName}
                  adminEmail={bookingResult.adminEmail}
                  onSkip={() => setStage("done")}
                  onBooked={() => setStage("done")}
                />
              )}

              {/* ── Form ── */}
              {stage === "form" && (
                <form onSubmit={handleSubmit} className="space-y-5">

                  {/* Full Name */}
                  <div>
                    <label className={labelBase}>Full Name<span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.adminName}
                      onChange={(e) => handleChange("adminName", e.target.value)}
                      placeholder="Enter your full name"
                      className={`${inputBase} ${errors.adminName ? "border-red-500" : "border-white/30"}`}
                    />
                    {errors.adminName && <p className={errorClass}>{errors.adminName}</p>}
                  </div>

                  {/* Work Email */}
                  <div>
                    <label className={labelBase}>Work Email<span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      value={formData.adminEmail}
                      onChange={(e) => handleChange("adminEmail", e.target.value)}
                      onBlur={() => {
                        if (formData.adminEmail.trim()) {
                          const err = validateWorkEmail(formData.adminEmail);
                          if (err) setErrors((prev) => ({ ...prev, adminEmail: err }));
                        }
                      }}
                      placeholder="Enter your work email"
                      className={`${inputBase} ${errors.adminEmail ? "border-red-500" : "border-white/30"}`}
                    />
                    {errors.adminEmail && <p className={errorClass}>{errors.adminEmail}</p>}
                  </div>

                  {/* Company Name */}
                  <div>
                    <label className={labelBase}>Company Name<span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => handleChange("companyName", e.target.value)}
                      placeholder="Enter your company name"
                      className={`${inputBase} ${errors.companyName ? "border-red-500" : "border-white/30"}`}
                    />
                    {errors.companyName && <p className={errorClass}>{errors.companyName}</p>}
                  </div>

                  {/* Industry + Company Size */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className={labelBase}>Industry<span className="text-red-500">*</span></label>
                      <select
                        value={formData.companyIndustry}
                        onChange={(e) => handleChange("companyIndustry", e.target.value)}
                        className={`${inputBase} ${errors.companyIndustry ? "border-red-500" : "border-white/30"}`}
                      >
                        <option value="" className="bg-[#141516]">Select industry</option>
                        {INDUSTRIES.map((opt) => (
                          <option key={opt} value={opt} className="bg-[#141516]">{opt}</option>
                        ))}
                      </select>
                      {errors.companyIndustry && <p className={errorClass}>{errors.companyIndustry}</p>}
                    </div>
                    <div>
                      <label className={labelBase}>Company Size<span className="text-red-500">*</span></label>
                      <select
                        value={formData.companySize}
                        onChange={(e) => handleChange("companySize", e.target.value)}
                        className={`${inputBase} ${errors.companySize ? "border-red-500" : "border-white/30"}`}
                      >
                        <option value="" className="bg-[#141516]">Select size</option>
                        {COMPANY_SIZES.map((opt) => (
                          <option key={opt} value={opt} className="bg-[#141516]">{opt}</option>
                        ))}
                      </select>
                      {errors.companySize && <p className={errorClass}>{errors.companySize}</p>}
                    </div>
                  </div>

                  {/* Company Location */}
                  <div>
                    <label className={labelBase}>Company Location <span className="text-white/40 font-normal">(optional)</span></label>
                    <input
                      type="text"
                      value={formData.companyLocation}
                      onChange={(e) => handleChange("companyLocation", e.target.value)}
                      placeholder="e.g. Mumbai, India"
                      className={`${inputBase} border-white/30`}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className={labelBase}>Phone Number<span className="text-red-500">*</span></label>
                    <div className="flex gap-2">
                      <select
                        value={formData.countryCode}
                        onChange={(e) => handleChange("countryCode", e.target.value)}
                        className="px-3 py-2 bg-transparent border border-white/30 rounded-[2px] focus:outline-none focus:ring-2 focus:ring-[#B8EF43] text-white font-fira-sans text-sm"
                      >
                        {COUNTRY_CODES.map((c) => (
                          <option key={c} value={c} className="bg-[#141516]">{c}</option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        value={formData.adminPhone}
                        onChange={(e) => handleChange("adminPhone", e.target.value)}
                        placeholder="XXXXXXXXXX"
                        className={`flex-1 rounded-[2px] px-3 py-2 bg-transparent border focus:outline-none focus:ring-2 focus:ring-[#B8EF43] text-white placeholder-white/40 font-fira-sans text-sm ${errors.adminPhone ? "border-red-500" : "border-white/30"}`}
                      />
                    </div>
                    {errors.adminPhone && <p className={errorClass}>{errors.adminPhone}</p>}
                  </div>

                  {/* Number of users */}
                  <div>
                    <label className={labelBase}>Expected Participants<span className="text-red-500">*</span></label>
                    <input
                      type="number"
                      min="1"
                      value={formData.numberOfUsers}
                      onChange={(e) => handleChange("numberOfUsers", e.target.value)}
                      placeholder="How many employees will take the assessment?"
                      className={`${inputBase} ${errors.numberOfUsers ? "border-red-500" : "border-white/30"}`}
                    />
                    {errors.numberOfUsers && <p className={errorClass}>{errors.numberOfUsers}</p>}
                  </div>

                  {/* Teams */}
                  <div>
                    <label className={labelBase}>
                      Teams to assess<span className="text-red-500">*</span>
                    </label>
                    <TeamsDropdown
                      selected={formData.teams}
                      onToggle={toggleTeam}
                      error={errors.teams}
                    />
                    {formData.teams.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {formData.teams.map((t) => (
                          <span key={t} className="flex items-center gap-1 bg-white/10 text-white/80 text-xs px-2 py-1 rounded-[3px]">
                            {t}
                            <button type="button" onClick={() => toggleTeam(t)} className="text-white/40 hover:text-white leading-none">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                    {errors.teams && <p className={errorClass}>{errors.teams}</p>}
                  </div>

                  {submitError && (
                    <div className="border border-red-500/30 bg-red-500/10 rounded-[4px] px-4 py-3">
                      <p className="text-red-400 text-sm">{submitError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || !canSubmit}
                    className="w-full bg-white text-black rounded-[2px] py-3 font-medium transition hover:bg-white/90 disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                  >
                    {hasUTM
                      ? (isSubmitting ? "Sending…" : "Send Enquiry")
                      : (isSubmitting ? "Setting up…" : "Create Assessment Link →")}
                  </button>
                </form>
              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default B2BAssessmentForm;
